// DOM rendering only: receives already-analysed data, never touches the network.
import type { Analysis } from '../analysis';
import type { TestStage } from '../measurement/types';
import { RATING_LABEL, formatDataUsed, formatMbps, formatMs } from './format';

export type ViewName = 'home' | 'testing' | 'result' | 'error';

const STAGE_ORDER: TestStage[] = ['connecting', 'latency', 'download', 'upload', 'analyzing'];
const STAGE_LABEL: Record<TestStage, string> = {
  connecting: 'Checking connection',
  latency: 'Testing latency',
  download: 'Testing download',
  upload: 'Testing upload',
  analyzing: 'Analyzing results',
};

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`#${id} missing from the page`);
  return found as T;
}

function setText(id: string, text: string): void {
  el(id).textContent = text;
}

export function showView(name: ViewName): void {
  for (const view of ['home', 'testing', 'result', 'error'] as const) {
    el(`view-${view}`).hidden = view !== name;
  }
  // Move focus to the new view's heading so screen readers announce the change.
  const heading = el(`view-${name}`).querySelector<HTMLElement>('[tabindex="-1"]');
  heading?.focus({ preventScroll: true });
  window.scrollTo({ top: 0 });
}

export function resetStages(): void {
  for (const item of el('view-testing').querySelectorAll<HTMLLIElement>('[data-stage]')) {
    item.classList.remove('is-active', 'is-done');
    item.removeAttribute('aria-current');
  }
  setText('stage-status', '');
}

export function showStage(stage: TestStage): void {
  const index = STAGE_ORDER.indexOf(stage);
  for (const item of el('view-testing').querySelectorAll<HTMLLIElement>('[data-stage]')) {
    const itemIndex = STAGE_ORDER.indexOf(item.dataset.stage as TestStage);
    item.classList.toggle('is-done', itemIndex < index);
    item.classList.toggle('is-active', itemIndex === index);
    if (itemIndex === index) item.setAttribute('aria-current', 'step');
    else item.removeAttribute('aria-current');
  }
  setText('stage-status', `${STAGE_LABEL[stage]}…`);
}

export function renderResult(analysis: Analysis): void {
  const { health, diagnosis, metrics } = analysis;
  const label = RATING_LABEL[health.rating];

  const card = el('view-result');
  card.dataset.rating = health.rating;
  setText('score-value', String(health.score));
  setText('rating', label);
  el('score').setAttribute('aria-label', `Internet Health Score: ${health.score} out of 100, ${label.toLowerCase()}`);
  const ring = el('score-ring');
  ring.style.strokeDasharray = '0 100';
  // Next frame, so the ring animates from empty (skipped under reduced motion by CSS).
  requestAnimationFrame(() => requestAnimationFrame(() => (ring.style.strokeDasharray = `${health.score} 100`)));

  setText('finding', diagnosis.finding);
  setText('action-label', diagnosis.severity === 'none' ? 'Next step' : 'Try this');
  setText('action', diagnosis.action);

  setText('m-download', formatMbps(metrics.downloadMbps));
  setText('m-upload', formatMbps(metrics.uploadMbps));
  setText('m-latency', formatMs(metrics.latencyMs));
  setText('m-jitter', formatMs(metrics.jitterMs));
  setText('m-loaded', formatMs(metrics.loadedLatencyMs));
  setText(
    'm-loaded-note',
    metrics.latencyIncreaseMs === undefined
      ? 'Couldn’t be measured reliably in this check.'
      : `Latency while downloading: +${formatMs(metrics.latencyIncreaseMs)} ms compared with idle.`,
  );
  setText('fineprint', `This check used about ${formatDataUsed(analysis.dataUsedBytes)} of data.`);
}

export function renderError(message: string): void {
  setText('error-message', message);
}

export function renderDebug(enabled: boolean, data: unknown): void {
  const panel = el('debug');
  panel.hidden = !enabled;
  if (enabled) setText('debug-output', JSON.stringify(data, null, 2));
}
